import { useState } from "react";
import { Text, TextInput, View } from "react-native";
import { KeyRound } from "lucide-react-native";
import {
  FieldMessage,
  GlassButton,
  MoonFieldInput,
  TextActionButton,
  haptic,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import AuthGlassScene from "../../onboarding/components/AuthGlassScene";
import {
  MovingGradientTitle,
  OnboardingPrimaryButton,
} from "../../onboarding/components/OnboardingLayout";

import {
  use2FAStatus,
  useAuthenticatePasskey,
  useRecover2FA,
  useVerify2FA,
} from "@src/packages/auth/hooks/use2FA";
import { OTPInput } from "@src/packages/form/OTPInput/OTPInput";
import { semanticColors } from "@louise/theme/tokens";

// Both Stytch SDKs (@stytch/react on web, @stytch/react-native on native)
// throw errors carrying a structured `error_type` slug
// (stytch.com/docs/api/errors) — classify on that, never on message prose.
const stytchErrorType = (err: unknown): string | null =>
  err instanceof Error &&
  "error_type" in err &&
  typeof err.error_type === "string"
    ? err.error_type
    : null;

// Stytch error_type slugs meaning the user's code was wrong or expired, for
// both TOTP authenticate and recovery-code recover. `unable_to_authenticate_totp`
// is what stytch.totp.authenticate returns for a wrong code (captured live off
// dev). Anything else (network, session, rate limit) falls through to generic.
const CODE_ERROR_TYPES = new Set<string>([
  "unable_to_authenticate_totp",
  "totp_code_not_found",
  "unable_to_authenticate_totp_recovery_code",
  "totp_recovery_code_not_found",
  // Captured from the web SDK in QA run 29940341443 when an unknown recovery
  // code was submitted. Without this current slug the UI incorrectly falls
  // through to the generic transport-error copy.
  "invalid_recovery_code",
]);

export default function TwoFAChallengeScreen() {
  const t = useTranslations();
  const [code, setCode] = useState("");
  const [mode, setMode] = useState<"totp" | "recovery">("totp");
  const [error, setError] = useState<string | null>(null);
  // A recovery code signs the user in but is not a second factor: Stytch files
  // `recovery_code` under the same category as the password (online_account),
  // and dashboard-api excludes it too (#382), so the session still fails the
  // MFA gate and the API answers 403 mfa_required. Stytch also refuses factor
  // management (totps.create / webauthn.register → weak_factor_combination)
  // from such a session, so a new authenticator cannot be enrolled here. The
  // only self-service way through is the existing authenticator (or passkey);
  // otherwise support has to reset MFA server-side.
  const [recovered, setRecovered] = useState(false);
  const verify2FA = useVerify2FA();
  const recover2FA = useRecover2FA();
  const authenticatePasskey = useAuthenticatePasskey();

  // Show only the factor(s) the user can actually use here, rather than always
  // offering both a TOTP input and a passkey button. A passkey is only offered
  // when one is registered FOR THIS HOST (passkeyUsableHere) — a passkey bound
  // to another domain can't be authenticated here, so surfacing it would just
  // dead-end. If we can't determine usable factors (status not ready), fall
  // back to the TOTP flow so the screen is never actionless.
  const { data: status } = use2FAStatus();
  const hasTotp = status?.totpEnrolled ?? false;
  const usablePasskey = status?.passkeyUsableHere ?? false;
  const showTotpFlow = hasTotp || !usablePasskey;
  const showPasskey = usablePasskey;

  const isPending =
    verify2FA.isPending ||
    recover2FA.isPending ||
    authenticatePasskey.isPending;

  // No explicit onSuccess: stytch.totp.authenticate adds the authenticator_app
  // factor to the session, useStytchSession re-emits, and the RootNavigator
  // unmounts this screen automatically.
  const handleVerify = async () => {
    try {
      setError(null);
      if (mode === "totp") {
        await verify2FA.mutateAsync({ totpCode: code });
      } else {
        await recover2FA.mutateAsync({ recoveryCode: code });
        // Recovery does not release the gate (see `recovered`): explain why
        // and fall back to the authenticator-code form instead of leaving the
        // user on a recovery form whose Verify appeared to succeed.
        setRecovered(true);
        setMode("totp");
        setCode("");
      }
      haptic("buzz");
    } catch (err) {
      // Classify on Stytch's structured `error_type` slug, never on message
      // prose — a wrong TOTP/recovery code carries one of the slugs below,
      // while a network/transport failure carries none, so we don't tell the
      // user their code was wrong when the request never landed. Mirrors the
      // CreateAccountScreen OTP fix.
      const errorType = stytchErrorType(err);
      const isCodeError = errorType !== null && CODE_ERROR_TYPES.has(errorType);
      if (isCodeError) {
        setError(
          mode === "totp"
            ? t("auth.two_fa_challenge.error_invalid_totp")
            : t("auth.two_fa_challenge.error_invalid_recovery"),
        );
      } else {
        setError(t("auth.two_fa_challenge.error_generic"));
      }
    }
  };

  // Passkey (WebAuthn) is a distinct factor CATEGORY ("hardware") from moon's
  // SMS first factor ("possession"), so a successful authenticate clears the
  // ≥2-category MFA gate. No explicit navigation here for the same reason as
  // the TOTP path: authenticate adds the webauthn factor to the session,
  // useStytchSession re-emits, and RootNavigator unmounts this screen.
  const handlePasskey = async () => {
    try {
      setError(null);
      await authenticatePasskey.mutateAsync();
      haptic("buzz");
    } catch {
      setError(t("auth.two_fa_challenge.error_passkey"));
    }
  };

  const isValid = mode === "totp" ? code.length === 6 : code.trim().length > 0;

  // After a recovery the header explains why the user is still here; otherwise
  // passkey-only users get a passkey-specific subtitle and the header follows
  // the TOTP/recovery mode as before.
  const title = recovered
    ? t("auth.two_fa_challenge.recovery_code_accepted")
    : !showTotpFlow || mode === "totp"
      ? t("auth.two_fa_challenge.title_totp")
      : t("auth.two_fa_challenge.title_recovery");
  const subtitle = recovered
    ? t("auth.two_fa_challenge.recovery_code_is_not_a_second_factor")
    : !showTotpFlow
      ? t("auth.two_fa_challenge.subtitle_passkey")
      : mode === "totp"
        ? t("auth.two_fa_challenge.subtitle_totp")
        : t("auth.two_fa_challenge.subtitle_recovery");

  // The second step of signing in, so it keeps the sign-in look: the same
  // living background, glass card, glass field and white button as the phone
  // step it follows, and no close, because finishing it is the only way on.
  return (
    <AuthGlassScene testID="two-fa-challenge">
      <MovingGradientTitle
        style={{
          fontFamily: "Tobias_300Light",
          fontSize: 34,
          color: semanticColors.onDark.text.primary,
          lineHeight: 40,
          letterSpacing: -0.8,
          textAlign: "center",
        }}
      >
        {title}
      </MovingGradientTitle>
      <Text
        className="font-dm-sans"
        style={{
          maxWidth: 380,
          fontSize: 14,
          lineHeight: 20,
          color: semanticColors.onDark.text.secondary,
          textAlign: "center",
          marginTop: 8,
          marginBottom: 24,
        }}
      >
        {subtitle}
      </Text>

      <View style={{ width: "100%", maxWidth: 380, gap: 14 }}>
        {showTotpFlow ? (
          <View>
            {mode === "totp" ? (
              // A six-digit code is six boxes, like sign-up and phone link.
              <OTPInput
                length={6}
                appearance="glass"
                autoFocus
                hasError={!!error}
                onChange={(next) => {
                  setCode(next);
                  setError(null);
                }}
              />
            ) : (
              <MoonFieldInput appearance="glass" hasError={!!error}>
                <TextInput
                  value={code}
                  onChangeText={(text) => {
                    setCode(text);
                    setError(null);
                  }}
                  placeholder={t("auth.two_fa_challenge.placeholder_recovery")}
                  placeholderTextColor={semanticColors.onDark.text.tertiary}
                  selectionColor="rgba(255, 218, 189, 0.82)"
                  keyboardType="default"
                  autoCapitalize="none"
                  autoCorrect={false}
                  autoFocus
                  className="font-dm-sans"
                  style={{
                    width: "100%",
                    minHeight: 56,
                    fontSize: 16,
                    fontWeight: "600",
                    textAlign: "center",
                    letterSpacing: 1,
                    color: semanticColors.onDark.text.primary,
                  }}
                />
              </MoonFieldInput>
            )}
            {error ? (
              <FieldMessage onDark centered style={{ marginTop: 10 }}>
                {error}
              </FieldMessage>
            ) : null}
          </View>
        ) : error ? (
          <FieldMessage onDark centered>
            {error}
          </FieldMessage>
        ) : null}

        {showTotpFlow ? (
          <OnboardingPrimaryButton
            title={t("auth.two_fa_challenge.verify")}
            onPress={() => void handleVerify()}
            disabled={!isValid || isPending}
            testID="two-fa-challenge-verify"
          />
        ) : null}

        {/* Passkey-only accounts verify with it; with an authenticator app
            too, it is the frosted alternative, as on the log in screen. */}
        {showPasskey ? (
          showTotpFlow ? (
            <GlassButton
              variant="frosted"
              size="large"
              label={t("auth.two_fa_challenge.use_passkey")}
              accessibilityLabel={t("auth.two_fa_challenge.use_passkey")}
              icon={
                <KeyRound
                  size={17}
                  color={semanticColors.onDark.text.primary}
                  strokeWidth={2}
                />
              }
              onPress={() => void handlePasskey()}
              disabled={isPending}
              loading={authenticatePasskey.isPending}
              testID="two-fa-challenge-passkey"
            />
          ) : (
            <OnboardingPrimaryButton
              title={t("auth.two_fa_challenge.use_passkey")}
              onPress={() => void handlePasskey()}
              disabled={isPending}
              testID="two-fa-challenge-passkey"
            />
          )
        ) : null}
      </View>

      {/* Another recovery code would only land back here, so the toggle goes
          away once one has been accepted. */}
      {showTotpFlow && !recovered ? (
        <TextActionButton
          label={
            mode === "totp"
              ? t("auth.two_fa_challenge.use_recovery_code")
              : t("auth.two_fa_challenge.back_to_totp")
          }
          action={
            mode === "totp"
              ? "auth.two_fa_challenge.use_recovery_code"
              : "auth.two_fa_challenge.back_to_totp"
          }
          tone="onDark"
          size="sm"
          centered
          testID="two-fa-challenge-mode-toggle"
          onPress={() => {
            setMode(mode === "totp" ? "recovery" : "totp");
            setCode("");
            setError(null);
          }}
          style={{ marginTop: 16 }}
        />
      ) : null}
    </AuthGlassScene>
  );
}
